/**
 * Shared Product Card HTML Builder
 * Renders product cards from JSON data (discover/similar API format)
 * Used by tags-discovery-grid, for-you-recommendations, and other client-rendered grids
 */

/**
 * Escape HTML to prevent XSS
 */
export function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Extract handle from product
 */
export function extractHandle(product) {
  if (product.handle && !product.handle.match(/^\d+$/)) {
    return product.handle;
  }
  if (product.url) {
    const match = product.url.match(/\/products\/([^?#/]+)/);
    if (match) return match[1];
  }
  return product.handle || product.id || '';
}

/**
 * Get like count from product
 */
export function getLikeCount(product) {
  // Top-level field (for-you API and other Vespa-formatted responses)
  if (typeof product.like_count === 'number') return product.like_count;
  // Nested attributes (Shopify metafield-style responses)
  const attr = product.attributes?.like_count;
  if (!attr) return 0;
  if (attr.numbers?.length) return parseInt(attr.numbers[0], 10) || 0;
  if (attr.text?.length) return parseInt(attr.text[0], 10) || 0;
  if (typeof attr === 'number') return attr;
  return 0;
}

/**
 * Humanize like count
 */
export function humanizeLikeCount(count) {
  if (count >= 1000) {
    return (count / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  }
  return count.toString();
}

/**
 * Get currency for current market
 */
export function getMarketCurrency() {
  const country = window.Shopify?.country?.toLowerCase();
  if (!country) return 'GBP';

  const countryToCurrency = {
    'gb': 'GBP', 'uk': 'GBP',
    'us': 'USD',
    'au': 'AUD',
    'ca': 'CAD',
    'nz': 'NZD',
    // EU countries use EUR
    'de': 'EUR', 'fr': 'EUR', 'es': 'EUR', 'it': 'EUR', 'nl': 'EUR',
    'be': 'EUR', 'at': 'EUR', 'pt': 'EUR', 'ie': 'EUR', 'fi': 'EUR',
    'gr': 'EUR', 'lu': 'EUR', 'mt': 'EUR', 'cy': 'EUR', 'sk': 'EUR',
    'si': 'EUR', 'ee': 'EUR', 'lv': 'EUR', 'lt': 'EUR',
  };

  return countryToCurrency[country] || 'GBP';
}

/**
 * Get locale path prefix from current URL (e.g. "/de-de", "/fr")
 */
export function getLocalePathPrefix() {
  const match = window.location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/);
  return match ? `/${match[1]}` : '';
}

/**
 * Add locale prefix to a root-relative product URL if needed.
 * e.g. "/products/foo" -> "/de-de/products/foo"
 */
export function localizeProductUrl(url) {
  if (!url) return url;
  // Already has locale prefix or is absolute
  if (/^https?:\/\//.test(url)) return url;
  const prefix = getLocalePathPrefix();
  if (!prefix) return url;
  // Avoid double-prefixing
  if (url.startsWith(prefix + '/') || url === prefix) return url;
  return prefix + url;
}

/**
 * Get locale for price formatting
 */
export function getLocaleForCurrency(currency) {
  const currencyToLocale = {
    'GBP': 'en-GB',
    'USD': 'en-US',
    'EUR': 'de-DE',
    'AUD': 'en-AU',
    'CAD': 'en-CA',
    'NZD': 'en-NZ',
  };
  return currencyToLocale[currency] || 'en-GB';
}

/**
 * Format price with currency
 */
export function formatPrice(price, basePricing) {
  const currency = basePricing?.currency || getMarketCurrency();
  const locale = getLocaleForCurrency(currency);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(price);
  } catch {
    return `\u00A3${price.toFixed(2)}`;
  }
}

/**
 * Add width param to image URL
 */
export function addWidthParam(url, width) {
  if (!url) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}width=${width}&quality=60`;
}

/**
 * Build srcset for responsive images
 */
export function buildSrcSet(url) {
  if (!url) return '';
  return [
    `${addWidthParam(url, 200)} 200w`,
    `${addWidthParam(url, 400)} 400w`,
    `${addWidthParam(url, 600)} 600w`,
    `${addWidthParam(url, 800)} 800w`
  ].join(', ');
}

/**
 * Build data attributes for market-specific pricing
 */
export function buildPriceDataAttributes(basePricing) {
  if (!basePricing || typeof basePricing !== 'object') return '';

  const attrs = [];
  const prices = basePricing.price || {};
  const compareAtPrices = basePricing.compare_at_price || {};

  const marketToCurrency = {
    'default': 'GBP', 'gb': 'GBP', 'us': 'USD', 'eu': 'EUR',
    'au': 'AUD', 'ca': 'CAD', 'nz': 'NZD'
  };

  Object.keys(prices).forEach(market => {
    const price = prices[market];
    const compareAt = compareAtPrices[market];
    const currency = marketToCurrency[market] || 'GBP';

    if (price !== undefined && price !== null) {
      attrs.push(`data-price-${market}="${price}"`);
      attrs.push(`data-currency-${market}="${currency}"`);
      if (compareAt !== undefined && compareAt > price) {
        attrs.push(`data-compare-at-price-${market}="${compareAt}"`);
      }
    }
  });

  if (attrs.length > 0) {
    attrs.push('data-has-market-pricing="true"');
  }

  return attrs.join(' ');
}

/**
 * Build mock image HTML with frame overlay
 */
export function buildMockImageHtml(artworkUrl, mockConfig, alt, loading, fetchpriority) {
  const frameUrl = mockConfig.image || '';
  const placementTop = mockConfig.placementTop || mockConfig.placement_top || 0;
  const placementLeft = mockConfig.placementLeft || mockConfig.placement_left || 0;
  const placementWidth = mockConfig.placementWidth || mockConfig.placement_width || 100;
  const aspectRatio = mockConfig.aspectRatio || mockConfig.aspect_ratio || 'auto';
  const frameAspectRatio = mockConfig.frameAspectRatio || mockConfig.frame_aspect_ratio || 1;

  const placementStyle = aspectRatio !== 'auto'
    ? `top: ${placementTop}%; left: ${placementLeft}%; width: ${placementWidth}%; --placement-aspect-ratio: ${aspectRatio};`
    : `top: ${placementTop}%; left: ${placementLeft}%; width: ${placementWidth}%;`;

  // Only mark as landscape when we have explicit frame ratio data (> 1.05).
  // Default ratio of 1 means data is absent — treat as portrait/square cover-crop.
  const orientation = frameAspectRatio > 1.05 ? 'landscape' : 'portrait';

  return `
    <div class="mock-image-container mock-image-container--css mock-image-container--${orientation}" style="--frame-aspect-ratio: ${frameAspectRatio};">
      <div class="mock-image__frame-container" style="background-image: url(${addWidthParam(frameUrl, 800)});">
        <div class="mock-image__placement" style="${placementStyle}">
          <img
            src="${addWidthParam(artworkUrl, 480)}"
            srcset="${buildSrcSet(artworkUrl)}"
            sizes="(min-width: 750px) 160px, 150px"
            alt="${escapeHtml(alt)}"
            class="mock-image__artwork"
            loading="${loading}"
            ${fetchpriority !== 'auto' ? `fetchpriority="${fetchpriority}"` : ''}
          />
        </div>
      </div>
    </div>
  `;
}

/**
 * Build simple image HTML without mock
 */
export function buildSimpleImageHtml(imageUrl, alt, loading, fetchpriority) {
  if (!imageUrl) {
    return `
      <div class="pcv2-img-placeholder"></div>
    `;
  }

  return `
    <img
      src="${addWidthParam(imageUrl, 480)}"
      srcset="${buildSrcSet(imageUrl)}"
      sizes="(min-width: 750px) 160px, 150px"
      alt="${escapeHtml(alt)}"
      class="pcv2-img product-card__image"
      loading="${loading}"
      ${fetchpriority !== 'auto' ? `fetchpriority="${fetchpriority}"` : ''}
    />
  `;
}

/**
 * Build wishlist button HTML matching the in-house wishlist-button component
 */
export function buildWishlistHtml(product, likeCount) {
  const productId = product.product_id || product.id || '';
  const handle = extractHandle(product);
  const imageUrl = product.image_url || product.imageUrl || '';
  const humanizedCount = humanizeLikeCount(likeCount);

  // CAPI wishlist join key + value (read by analytics-custom-events.js): variants[0].id is the SKU (=legacy_id; vespa.server.js maps id:variant.sku), from_price the decimal price.
  const sku = product.variants?.[0]?.id || '';
  const price = product.from_price != null && product.from_price !== '' ? String(product.from_price) : '';
  const currency = getMarketCurrency();

  return `
    <wishlist-button
      class="wishlist-button wishlist-button--compact"
      data-product-handle="${escapeHtml(handle)}"
      data-product-id="${escapeHtml(productId)}"
      data-product-sku="${escapeHtml(sku)}"
      data-product-price="${escapeHtml(price)}"
      data-currency="${escapeHtml(currency)}"
      data-like-count="0"
      data-product-image="${escapeHtml(imageUrl)}"
      data-compact
    >
      <div class="wishlist-button__inner">
        <button type="button" class="wishlist-button__btn" aria-label="Add to wishlist">
          <svg class="wishlist-button__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
        </button>
      </div>
    </wishlist-button>
  `;
}

/**
 * Build HTML for a single product card from JSON data
 * @param {Object} product - Product data from discover/similar API
 * @param {number} index - Index for loading priority
 * @returns {string} HTML string for product card
 */
export function buildProductCardHtml(product, index = 0) {
  const url = localizeProductUrl(product.url || `/products/${extractHandle(product)}`);
  const title = product.title || '';
  const imageUrl = product.image_url || product.imageUrl || '';
  const productId = product.product_id || product.id || '';
  const handle = extractHandle(product);
  const likeCount = getLikeCount(product);
  const loading = index < 2 ? 'eager' : 'lazy';
  const fetchpriority = index === 0 ? 'high' : 'auto';

  const artist = product.artist_name || product.artist || product.brand || product.vendor || '';

  const fromPrice = product.from_price;
  const compareAtPrice = product.from_compare_at_price;
  const basePricing = product.base_pricing || product.variants?.[0]?.configurator?.basePricing;
  const hasValidPrice = fromPrice && fromPrice > 0;
  const hasCompareAt = compareAtPrice && compareAtPrice > fromPrice;
  const onSale = hasCompareAt;

  const mockConfig = product.mock_config;
  const imageHtml = mockConfig && imageUrl
    ? buildMockImageHtml(imageUrl, mockConfig, title, loading, fetchpriority)
    : buildSimpleImageHtml(imageUrl, title, loading, fetchpriority);

  const wishlistHtml = buildWishlistHtml(product, likeCount);

  const badgeHtml = product.is_bestseller
    ? `<div class="pcv2-badge">Bestseller</div>`
    : onSale
      ? `<div class="pcv2-badge pcv2-badge--sale">Sale</div>`
      : '';

  const priceHtml = hasValidPrice
    ? `<span class="pcv2-price${onSale ? ' pcv2-price--sale' : ''}">
        <span class="pcv2-from">From</span>
        <span class="pcv2-price-now">${formatPrice(fromPrice, basePricing)}</span>
        ${hasCompareAt ? `<s class="pcv2-price-was">${formatPrice(compareAtPrice, basePricing)}</s>` : ''}
      </span>`
    : '';

  return `
    <div class="resource-list__item">
      <a class="product-card-v2" href="${escapeHtml(url)}" data-product-id="${escapeHtml(productId)}" data-product-handle="${escapeHtml(handle)}">
        <div class="pcv2-art">
          ${imageHtml}
          ${wishlistHtml}
          ${badgeHtml}
        </div>
        <div class="pcv2-name">${escapeHtml(title)}</div>
        <div class="pcv2-meta">
          <span class="pcv2-artist">${escapeHtml(artist)}</span>
          ${priceHtml}
        </div>
      </a>
    </div>
  `;
}

/**
 * Build HTML for product grid from array of products
 * @param {Array} products - Array of product data
 * @returns {string} HTML string for product grid
 */
export function buildProductGridHtml(products) {
  return products.map((product, index) => buildProductCardHtml(product, index)).join('');
}
